/**
 * @fileoverview 運営管理者 端末・資材管理（統合）
 * @description Companion実機（MDM: 自動登録・遠隔制限/操作/アプリ導入）と、
 *   資材台帳（管理番号・貸与先・在庫/修理/廃棄）を1画面に統合表示する。
 *   - Companion端末は自動で行に出る。台帳項目（管理番号・会社・状態）を紐づけ登録できる。
 *   - Companion非搭載の物（PC・予備在庫等）は「資材を追加」で手動登録。
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Smartphone, Lock, Unlock, RefreshCw, Trash2, MonitorSmartphone, PackagePlus, X, Plus, ClipboardList, HelpCircle, Copy } from 'lucide-react';
import {
  listMdmDevices, updateMdmDevice, deleteMdmDevice, type MdmDevice, type MdmMode,
  listCatalog, addCatalogApp, deleteCatalogApp, installToDevice, installToAll, listInstallJobs,
  type CatalogApp, type InstallJob,
  listAssets, createAsset, updateAsset, deleteAsset, listCompanies, type Asset, type Company,
  listSharedTerminals, type SharedTerminal,
} from '../services/api';

/** アルコール検知器の残り回数の色。900回以上で警告、寿命到達で赤にする */
function detectorStyle(count: number, max: number): { bg: string; fg: string; bd: string; icon: string } {
  if (count >= max) return { bg: '#fef2f2', fg: '#dc2626', bd: '#fecaca', icon: '🚫' };
  if (count >= 900) return { bg: '#fffbeb', fg: '#b45309', bd: '#fde68a', icon: '⚠️' };
  return { bg: '#f0fdf4', fg: '#15803d', bd: '#bbf7d0', icon: '🧪' };
}

/** 充電残量の色（少ないほど赤く。船内で電池切れになると打刻ができなくなる） */
function batteryStyle(pct: number): { bg: string; fg: string; bd: string; icon: string } {
  if (pct <= 15) return { bg: '#fef2f2', fg: '#dc2626', bd: '#fecaca', icon: '🪫' };
  if (pct <= 30) return { bg: '#fffbeb', fg: '#b45309', bd: '#fde68a', icon: '🪫' };
  return { bg: '#f0fdf4', fg: '#15803d', bd: '#bbf7d0', icon: '🔋' };
}

function relTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso.replace(' ', 'T') + 'Z').getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'たった今';
  if (m < 60) return `${m}分前`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}時間前`;
  return `${Math.floor(h / 24)}日前`;
}

const MODE_LABEL: Record<string, { text: string; bg: string; fg: string; bd: string }> = {
  locked:  { text: '🔒 制限中',   bg: '#fef2f2', fg: '#dc2626', bd: '#fecaca' },
  relaxed: { text: '🔓 一部解除', bg: '#fffbeb', fg: '#d97706', bd: '#fde68a' },
  open:    { text: '🟢 通常',     bg: '#f0fdf4', fg: '#16a34a', bd: '#bbf7d0' },
};
const ASSET_STATUS: Record<string, { label: string; bg: string; fg: string }> = {
  in_stock: { label: '在庫',   bg: '#f1f5f9', fg: '#475569' },
  lent:     { label: '貸与中', bg: '#dbeafe', fg: '#1d4ed8' },
  repair:   { label: '修理中', bg: '#fef3c7', fg: '#b45309' },
  retired:  { label: '廃棄',   bg: '#fee2e2', fg: '#b91c1c' },
};
const TYPE_LABEL: Record<string, string> = { tablet: 'タブレット', smartphone: 'スマホ', pc: 'PC', other: 'その他' };

// 統合行: mdm実機 / 資材台帳（片方 or 両方）
interface Row { key: string; mdm?: MdmDevice; asset?: Asset; }

const DevicesPage: React.FC = () => {
  const [mdmDevices, setMdmDevices] = useState<MdmDevice[]>([]);
  const [terminals, setTerminals] = useState<SharedTerminal[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [unlockTarget, setUnlockTarget] = useState<MdmDevice | null>(null);
  const [installTarget, setInstallTarget] = useState<MdmDevice | null>(null);
  const [ledger, setLedger] = useState<{ mdm?: MdmDevice; asset?: Asset } | null>(null);
  const [showCatalog, setShowCatalog] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [m, a, t] = await Promise.all([listMdmDevices(), listAssets(), listSharedTerminals().catch(() => [])]);
      setMdmDevices(m); setAssets(a); setTerminals(t);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => {
    reload();
    listCompanies().then(setCompanies).catch(() => {});
    const id = setInterval(reload, 20000);
    return () => clearInterval(id);
  }, [reload]);

  // 統合: MDM実機 + （MDM未紐づけの）資材
  const assetByMdm = new Map(assets.filter(a => a.mdm_device_id).map(a => [a.mdm_device_id!, a]));
  const rows: Row[] = [
    ...mdmDevices.map(m => ({ key: `m-${m.device_id}`, mdm: m, asset: assetByMdm.get(m.device_id) })),
    ...assets.filter(a => !a.mdm_device_id).map(a => ({ key: `a-${a.id}`, asset: a })),
  ];

  const setMode = async (m: MdmDevice, mode: MdmMode) => {
    if (mode === 'locked' && !window.confirm(`「${m.label || m.model}」を制限モードにしますか？\n許可アプリ以外が非表示になり、アプリ追加・初期化も禁止されます（反映まで最大1分）。`)) return;
    setBusy(m.device_id);
    try { await updateMdmDevice(m.device_id, { mode }); await reload(); }
    catch (e: any) { alert(e?.message ?? '変更に失敗しました'); }
    setBusy(null); setUnlockTarget(null);
  };

  const removeMdm = async (m: MdmDevice) => {
    if (!window.confirm(`「${m.label || m.model}」を端末一覧から削除しますか？（再通信で再登録されます）`)) return;
    setBusy(m.device_id);
    try { await deleteMdmDevice(m.device_id); await reload(); } catch (e: any) { alert(e?.message); }
    setBusy(null);
  };
  const removeAsset = async (a: Asset) => {
    if (!window.confirm(`資材「${a.asset_no}」を台帳から削除しますか？`)) return;
    try { await deleteAsset(a.id); await reload(); } catch (e: any) { alert(e?.message); }
  };

  const REMOTE_ENABLED = true;
  const openRemote = (m: MdmDevice) => window.open(`/devices/${encodeURIComponent(m.device_id)}/remote`, '_blank', 'noopener');

  const th: React.CSSProperties = { padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' };
  const td: React.CSSProperties = { padding: '10px 12px', fontSize: 13, borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' };
  const btn = (bg: string, fg: string, disabled?: boolean): React.CSSProperties => ({ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', background: bg, color: fg, border: 'none', borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700 });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <Smartphone size={22} color="#1d4ed8" />
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a' }}>端末・資材管理</h1>
        <button onClick={() => setShowGuide(v => !v)} style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', background: showGuide ? '#eff6ff' : '#fff', border: `1px solid ${showGuide ? '#bfdbfe' : '#e2e8f0'}`, borderRadius: 8, cursor: 'pointer', fontSize: 13, color: showGuide ? '#1d4ed8' : '#475569', fontWeight: 600 }}>
          <HelpCircle size={14} /> セットアップ手順
        </button>
        <button onClick={() => setShowCatalog(true)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', fontSize: 13, color: '#475569', fontWeight: 600 }}>
          <PackagePlus size={14} /> アプリカタログ
        </button>
        <button onClick={() => setLedger({})} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', fontSize: 13, color: '#475569', fontWeight: 600 }}>
          <Plus size={14} /> 資材を追加
        </button>
        <button onClick={reload} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', fontSize: 13, color: '#475569', fontWeight: 600 }}>
          <RefreshCw size={13} /> 更新
        </button>
      </div>
      <p style={{ margin: '0 0 20px', fontSize: 13, color: '#64748b' }}>
        Companion導入端末は自動で一覧に表示されます。管理番号・貸与先などの台帳情報を紐づけつつ、制限・リモート操作・アプリ導入も行えます。PCや予備在庫など非Companionの物は「資材を追加」で登録します。
      </p>

      {/* 船に置いてある共有管理端末。目的は充電残量の把握（電池切れだと打刻ができなくなる）。
          情報源は船員アプリのハートビートなので、Companion実機の一覧とは別に出す。 */}
      {terminals.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 18px', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <MonitorSmartphone size={16} color="#0f766e" />
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#0f172a' }}>共有管理端末の状態</h2>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>（船内の打刻用タブレット・{terminals.length}台）</span>
          </div>
          <p style={{ margin: '0 0 10px', fontSize: 12, color: '#64748b' }}>
            残量が少ない順に並びます。電池が切れると船員が打刻できなくなるため、少ない端末は会社へ充電を促してください。<br />
            アルコール検知器は寿命1000回の使い捨てです。900回を超えた端末には交換品を手配してください。
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['会社', '船', '端末', '管理番号', '充電残量', 'アルコール検知器', '最終通信'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: 11, borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {terminals.map(t => {
                  const pct = t.last_battery;
                  const b = pct === null || pct === undefined ? null : batteryStyle(pct);
                  return (
                    <tr key={t.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{t.company_name}</td>
                      <td style={{ padding: '8px 12px', whiteSpace: 'nowrap', color: '#0ea5e9', fontWeight: 600 }}>{t.vessel_name ?? '—'}</td>
                      <td style={{ padding: '8px 12px' }}>{t.device_name ?? '—'}</td>
                      <td style={{ padding: '8px 12px', color: '#64748b' }}>{t.management_number || '—'}</td>
                      <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                        {b ? (
                          <span title={`${relTime(t.last_battery_at)}時点`}
                            style={{ fontSize: 12.5, fontWeight: 800, padding: '3px 10px', borderRadius: 999, background: b.bg, color: b.fg, border: `1px solid ${b.bd}` }}>
                            {b.icon} {pct}%{t.last_battery_charging ? ' ⚡充電中' : ''}
                          </span>
                        ) : <span style={{ color: '#94a3b8', fontSize: 12 }}>—（未報告）</span>}
                      </td>
                      <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                        {t.alcohol_count === null || t.alcohol_count === undefined ? (
                          <span style={{ color: '#94a3b8', fontSize: 12 }}>—（記録なし）</span>
                        ) : (() => {
                          const max = t.alcohol_max_count ?? 1000;
                          const ds = detectorStyle(t.alcohol_count, max);
                          return (
                            <span title={`最終測定 ${relTime(t.alcohol_last_measured_at)}／残り${Math.max(0, max - t.alcohol_count)}回`}
                              style={{ fontSize: 12.5, fontWeight: 800, padding: '3px 10px', borderRadius: 999, background: ds.bg, color: ds.fg, border: `1px solid ${ds.bd}` }}>
                              {ds.icon} {t.alcohol_count}/{max}回
                            </span>
                          );
                        })()}
                      </td>
                      <td style={{ padding: '8px 12px', color: '#94a3b8', whiteSpace: 'nowrap' }}>{relTime(t.last_seen)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showGuide && <SetupGuide onClose={() => setShowGuide(false)} />}

      {loading ? (
        <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>読み込み中...</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8', background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0' }}>
          端末・資材がありません。<br />Companionを導入すると端末が自動表示され、「資材を追加」で在庫も登録できます。
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#f8fafc' }}>
              <th style={th}>端末 / 資材</th><th style={th}>会社</th><th style={th}>状態</th><th style={th}>接続</th><th style={th}>操作</th>
            </tr></thead>
            <tbody>
              {rows.map(({ key, mdm, asset }) => {
                const m = mdm ? (MODE_LABEL[mdm.applied_mode] ?? MODE_LABEL.open!) : null;
                const pending = mdm && mdm.desired_mode !== mdm.applied_mode;
                const st = asset ? ASSET_STATUS[asset.status] : null;
                const title = mdm?.label || asset?.asset_no || mdm?.model || '—';
                const company = mdm?.company_name || asset?.company_name || '—';
                return (
                  <tr key={key}>
                    <td style={td}>
                      <div style={{ fontWeight: 700, color: '#0f172a' }}>
                        {title}
                        {mdm && <span style={{ marginLeft: 6, fontSize: 10, color: '#7c3aed', fontWeight: 700 }}>● Companion</span>}
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>
                        {mdm?.model ?? TYPE_LABEL[asset?.asset_type ?? ''] ?? ''}
                        {mdm?.phone_number ? ` / ${mdm.phone_number}` : ''}
                        {asset?.asset_no ? ` / 管理No.${asset.asset_no}` : (mdm && !asset ? ' / 台帳未登録' : '')}
                      </div>
                    </td>
                    <td style={{ ...td, color: '#475569' }}>{company}</td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
                        {m && <span style={{ padding: '3px 9px', borderRadius: 999, fontSize: 12, fontWeight: 700, background: m.bg, color: m.fg, border: `1px solid ${m.bd}` }}>{m.text}</span>}
                        {st && <span style={{ padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: st.bg, color: st.fg }}>{st.label}</span>}
                        {pending && <span style={{ fontSize: 10, color: '#d97706', fontWeight: 600 }}>⏳反映待ち</span>}
                      </div>
                    </td>
                    <td style={{ ...td, fontSize: 12, whiteSpace: 'nowrap', color: mdm?.online ? '#16a34a' : '#94a3b8' }}>
                      {mdm ? (mdm.online ? '● オンライン' : `○ ${relTime(mdm.last_seen)}`) : '—'}
                    </td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        {mdm && (mdm.desired_mode === 'locked'
                          ? <button onClick={() => setUnlockTarget(mdm)} disabled={busy === mdm.device_id} style={btn('#f0fdf4', '#16a34a')}><Unlock size={13} /> 解除</button>
                          : <button onClick={() => setMode(mdm, 'locked')} disabled={busy === mdm.device_id || !mdm.is_device_owner} style={btn(mdm.is_device_owner ? '#dc2626' : '#e2e8f0', mdm.is_device_owner ? '#fff' : '#94a3b8', !mdm.is_device_owner)}><Lock size={13} /> 制限</button>)}
                        {mdm && <button onClick={() => setInstallTarget(mdm)} disabled={!mdm.is_device_owner} style={btn(mdm.is_device_owner ? '#7c3aed' : '#e2e8f0', mdm.is_device_owner ? '#fff' : '#94a3b8', !mdm.is_device_owner)}><PackagePlus size={13} /> 導入</button>}
                        {mdm && REMOTE_ENABLED && <button onClick={() => openRemote(mdm)} disabled={!mdm.online} style={btn(mdm.online ? '#1d4ed8' : '#e2e8f0', mdm.online ? '#fff' : '#94a3b8', !mdm.online)}><MonitorSmartphone size={13} /> 操作</button>}
                        <button onClick={() => setLedger({ mdm, asset })} style={{ ...btn('#fff', '#475569'), border: '1px solid #e2e8f0' }}><ClipboardList size={13} /> 台帳</button>
                        {(asset && !mdm) && <button onClick={() => removeAsset(asset)} style={{ padding: '6px 8px', background: 'none', color: '#94a3b8', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer' }}><Trash2 size={13} /></button>}
                        {mdm && <button onClick={() => removeMdm(mdm)} disabled={busy === mdm.device_id} title="端末一覧から削除" style={{ padding: '6px 8px', background: 'none', color: '#94a3b8', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer' }}><Trash2 size={13} /></button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {unlockTarget && <UnlockDialog device={unlockTarget} onClose={() => setUnlockTarget(null)} onPick={m => setMode(unlockTarget, m)} />}
      {installTarget && <InstallDialog device={installTarget} onClose={() => setInstallTarget(null)} />}
      {ledger && <LedgerDialog mdm={ledger.mdm} asset={ledger.asset} companies={companies} onClose={() => setLedger(null)} onSaved={() => { setLedger(null); reload(); }} />}
      {showCatalog && <CatalogDialog onClose={() => setShowCatalog(false)} />}
    </div>
  );
};

// ── セットアップ手順ガイド ──
const CmdLine: React.FC<{ cmd: string }> = ({ cmd }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#0f172a', borderRadius: 7, padding: '8px 10px', margin: '6px 0' }}>
      <code style={{ flex: 1, color: '#7dd3fc', fontSize: 12, fontFamily: 'monospace', wordBreak: 'break-all' }}>{cmd}</code>
      <button onClick={() => { navigator.clipboard?.writeText(cmd); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
        style={{ display: 'flex', alignItems: 'center', gap: 4, background: copied ? '#16a34a' : '#1e293b', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 9px', fontSize: 11, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
        <Copy size={12} /> {copied ? 'コピー済' : 'コピー'}
      </button>
    </div>
  );
};

const SetupGuide: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const stepBox: React.CSSProperties = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px', marginBottom: 10 };
  const num: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%', background: '#1d4ed8', color: '#fff', fontSize: 12, fontWeight: 800, marginRight: 8, flexShrink: 0 };
  const stepTitle: React.CSSProperties = { display: 'flex', alignItems: 'center', fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 6 };
  const desc: React.CSSProperties = { fontSize: 12.5, color: '#475569', lineHeight: 1.7, marginLeft: 30 };

  return (
    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: 20, marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#1e3a5f' }}>📱 新しい端末のセットアップ手順</div>
        <button onClick={onClose} style={{ marginLeft: 'auto', border: 'none', background: 'none', cursor: 'pointer', color: '#64748b' }}><X size={16} /></button>
      </div>
      <p style={{ fontSize: 12.5, color: '#1e40af', margin: '0 0 14px', lineHeight: 1.7 }}>
        端末を「遠隔で制限・操作できる状態」にするための初回作業です。<b>PCと端末を接続して①〜④を一度だけ</b>行えば、以降はこの画面から全部遠隔でできます。（PC操作の詳細は担当エンジニア／CATMS社へ）
      </p>

      <div style={stepBox}>
        <div style={stepTitle}><span style={num}>1</span>端末のアカウントを削除</div>
        <div style={desc}>端末の「設定 → アカウント」から Google 等の<b>全アカウントを削除</b>します（管理者化の前提条件）。</div>
      </div>

      <div style={stepBox}>
        <div style={stepTitle}><span style={num}>2</span>Companion アプリを導入して起動</div>
        <div style={desc}>
          下記URLからAPKをインストールし、<b>一度アプリを起動</b>します。起動すると数十秒でこの一覧に自動表示されます（電話番号も自動取得）。
          <div style={{ marginTop: 4 }}><a href="https://dl.unison-sea.com/apk/catms-companion.apk" style={{ color: '#1d4ed8', fontWeight: 700 }}>https://dl.unison-sea.com/apk/catms-companion.apk</a></div>
        </div>
      </div>

      <div style={stepBox}>
        <div style={stepTitle}><span style={num}>3</span>端末管理者に昇格（PCから）</div>
        <div style={desc}>
          PCで端末に接続し、下記コマンドを実行します。<b>Success</b> と出れば、制限・アプリ導入・初期化禁止が遠隔で使えるようになります。
          <div style={{ marginLeft: -30, marginTop: 2 }}><CmdLine cmd="adb shell dpm set-device-owner jp.co.unison.catms.companion/.CatmsAdminReceiver" /></div>
        </div>
      </div>

      <div style={stepBox}>
        <div style={stepTitle}><span style={num}>4</span>アクセシビリティをON（リモート操作を使う場合）</div>
        <div style={desc}>
          端末の「設定 → アクセシビリティ →<b> CATMS Companion </b>→ ON」→ 許可ダイアログで許可。
          <br /><span style={{ color: '#b45309' }}>※この操作だけは端末画面での手動トグルが必要です（端末の仕様上、コマンドから有効化できないため）。</span>
        </div>
      </div>

      <div style={{ background: '#fff', border: '1px dashed #93c5fd', borderRadius: 10, padding: '12px 16px', fontSize: 12.5, color: '#334155', lineHeight: 1.8 }}>
        <b>完了後にこの画面からできること：</b><br />
        🔒 制限 / 🔓 解除（「制限のみ」か「全アプリ復元」を選択）　·　📦 導入（アプリカタログからサイレント導入）　·　🖥 操作（ライブ画面操作）　·　台帳（管理番号・貸与先の登録）<br />
        <span style={{ color: '#64748b' }}>スターリンク／モバイル回線でも動作します（同一ネットワーク不要）。</span>
      </div>

      <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 12 }}>
        必要な仕込み早見表：一覧に出す＝②まで／制限・アプリ導入＝③まで／ライブ画面操作＝③＋④
      </div>
    </div>
  );
};

// ── 共通オーバーレイ ──
const Overlay: React.FC<{ onClose: () => void; children: React.ReactNode; width?: number }> = ({ onClose, children, width = 460 }) => (
  <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
    <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 24, width, maxHeight: '85vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>{children}</div>
  </div>
);

const UnlockDialog: React.FC<{ device: MdmDevice; onClose: () => void; onPick: (m: MdmMode) => void }> = ({ device, onClose, onPick }) => (
  <Overlay onClose={onClose}>
    <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>制限の解除</div>
    <div style={{ fontSize: 13, color: '#64748b', marginBottom: 18 }}>「{device.label || device.model}」の解除方法を選んでください。</div>
    <button onClick={() => onPick('relaxed')} style={{ width: '100%', textAlign: 'left', padding: '14px 16px', marginBottom: 10, border: '1.5px solid #fde68a', background: '#fffbeb', borderRadius: 10, cursor: 'pointer' }}>
      <div style={{ fontWeight: 700, color: '#b45309', fontSize: 14 }}>🔓 制限のみ解除（業務アプリのまま）</div>
      <div style={{ fontSize: 12, color: '#92400e', marginTop: 3 }}>ホーム画面は業務アプリのまま。初期化禁止・インストール禁止・画面オフ抑止だけを解除します。</div>
    </button>
    <button onClick={() => onPick('open')} style={{ width: '100%', textAlign: 'left', padding: '14px 16px', border: '1.5px solid #bbf7d0', background: '#f0fdf4', borderRadius: 10, cursor: 'pointer' }}>
      <div style={{ fontWeight: 700, color: '#16a34a', fontSize: 14 }}>🟢 制限解除＋全アプリ復元</div>
      <div style={{ fontSize: 12, color: '#15803d', marginTop: 3 }}>Google Play・au系・デフォルトアプリを含め、通常のスマートフォンに戻します。</div>
    </button>
    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 12 }}>反映まで最大1分かかります。</div>
  </Overlay>
);

// ── 台帳（資材）編集/登録ダイアログ ──
const LedgerDialog: React.FC<{ mdm?: MdmDevice; asset?: Asset; companies: Company[]; onClose: () => void; onSaved: () => void }> = ({ mdm, asset, companies, onClose, onSaved }) => {
  const [f, setF] = useState<{ asset_no: string; asset_type: string; model: string; serial_no: string; status: string; company_id: string; note: string }>({
    asset_no: asset?.asset_no ?? '', asset_type: asset?.asset_type ?? (mdm ? 'smartphone' : 'tablet'),
    model: asset?.model ?? mdm?.model ?? '', serial_no: asset?.serial_no ?? '',
    status: asset?.status ?? (mdm ? 'lent' : 'in_stock'), company_id: asset?.company_id ?? '', note: asset?.note ?? '',
  });
  const [saving, setSaving] = useState(false);
  const inp: React.CSSProperties = { width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' };
  const lbl: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 4 };

  const save = async () => {
    if (!f.asset_no.trim()) { alert('管理番号は必須です'); return; }
    setSaving(true);
    try {
      if (asset) {
        await updateAsset(asset.id, {
          asset_no: f.asset_no.trim(), asset_type: f.asset_type, model: f.model.trim() || null,
          serial_no: f.serial_no.trim() || null, status: f.status, company_id: f.company_id || null, note: f.note.trim() || null,
        });
      } else {
        const { id } = await createAsset({ asset_no: f.asset_no.trim(), asset_type: f.asset_type, model: f.model.trim() || undefined, serial_no: f.serial_no.trim() || undefined, note: f.note.trim() || undefined });
        // Companion端末から登録した場合は自動で紐づけ
        await updateAsset(id, { status: f.status, company_id: f.company_id || null, ...(mdm ? { mdm_device_id: mdm.device_id } : {}) });
      }
      onSaved();
    } catch (e: any) { alert(e?.message ?? '保存に失敗しました'); setSaving(false); }
  };

  return (
    <Overlay onClose={onClose} width={480}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{asset ? '台帳を編集' : mdm ? 'この端末を台帳に登録' : '資材を追加'}</div>
        <button onClick={onClose} style={{ marginLeft: 'auto', border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={18} /></button>
      </div>
      {mdm && <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12, padding: '8px 12px', background: '#f8fafc', borderRadius: 8 }}>端末: {mdm.label || mdm.model}{mdm.phone_number ? ` / ${mdm.phone_number}` : ''}（自動で紐づきます）</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div><label style={lbl}>管理番号 *</label><input style={inp} value={f.asset_no} onChange={e => setF(p => ({ ...p, asset_no: e.target.value }))} placeholder="例: T-001" /></div>
        <div><label style={lbl}>種別</label><select style={inp} value={f.asset_type} onChange={e => setF(p => ({ ...p, asset_type: e.target.value }))}>{Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
        <div><label style={lbl}>機種</label><input style={inp} value={f.model} onChange={e => setF(p => ({ ...p, model: e.target.value }))} /></div>
        <div><label style={lbl}>シリアル</label><input style={inp} value={f.serial_no} onChange={e => setF(p => ({ ...p, serial_no: e.target.value }))} /></div>
        <div><label style={lbl}>状態</label><select style={inp} value={f.status} onChange={e => setF(p => ({ ...p, status: e.target.value }))}>{Object.entries(ASSET_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></div>
        <div><label style={lbl}>貸与先の会社</label><select style={inp} value={f.company_id} onChange={e => setF(p => ({ ...p, company_id: e.target.value }))}><option value="">（未貸与）</option>{companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
        <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>備考</label><input style={inp} value={f.note} onChange={e => setF(p => ({ ...p, note: e.target.value }))} /></div>
      </div>
      <button onClick={save} disabled={saving} style={{ width: '100%', marginTop: 16, padding: '11px', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>{saving ? '保存中...' : '保存する'}</button>
    </Overlay>
  );
};

// ── アプリ導入ダイアログ ──
const InstallDialog: React.FC<{ device: MdmDevice; onClose: () => void }> = ({ device, onClose }) => {
  const [catalog, setCatalog] = useState<CatalogApp[]>([]);
  const [jobs, setJobs] = useState<InstallJob[]>([]);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const loadJobs = useCallback(() => { listInstallJobs(device.device_id).then(setJobs).catch(() => {}); }, [device.device_id]);
  useEffect(() => { listCatalog().then(setCatalog).catch(() => {}); loadJobs(); const id = setInterval(loadJobs, 8000); return () => clearInterval(id); }, [loadJobs]);
  const toggle = (id: string) => setSel(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const submit = async () => {
    if (sel.size === 0) return; setBusy(true);
    try { const r = await installToDevice(device.device_id, [...sel]); alert(`${r.queued}件を導入キューに追加しました。端末がオンラインになると自動でインストールされます。`); setSel(new Set()); loadJobs(); }
    catch (e: any) { alert(e?.message ?? '失敗しました'); }
    setBusy(false);
  };
  const jobStatus: Record<string, string> = { pending: '待機中', done: '完了' };
  return (
    <Overlay onClose={onClose} width={520}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>アプリ導入 — {device.label || device.model}</div>
        <button onClick={onClose} style={{ marginLeft: 'auto', border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={18} /></button>
      </div>
      {catalog.length === 0 ? (
        <div style={{ fontSize: 13, color: '#64748b', padding: '12px 0' }}>カタログにアプリがありません。右上の「アプリカタログ」から追加してください。</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
          {catalog.map(a => (
            <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={sel.has(a.id)} onChange={() => toggle(a.id)} />
              <div><div style={{ fontSize: 13, fontWeight: 600 }}>{a.name}</div><div style={{ fontSize: 11, color: '#94a3b8' }}>{a.package_name}</div></div>
            </label>
          ))}
        </div>
      )}
      <button onClick={submit} disabled={busy || sel.size === 0} style={{ width: '100%', padding: '11px', background: sel.size ? '#7c3aed' : '#e2e8f0', color: sel.size ? '#fff' : '#94a3b8', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: sel.size ? 'pointer' : 'not-allowed' }}>選択した{sel.size}件を端末へ導入</button>
      {jobs.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>導入状況</div>
          {jobs.map(j => (
            <div key={j.id} style={{ display: 'flex', fontSize: 12, padding: '4px 0', borderBottom: '1px solid #f1f5f9' }}>
              <span>{j.name || j.package_name}</span>
              <span style={{ marginLeft: 'auto', color: j.status === 'done' ? '#16a34a' : '#d97706', fontWeight: 600 }}>{jobStatus[j.status] ?? j.status}</span>
            </div>
          ))}
        </div>
      )}
    </Overlay>
  );
};

// ── カタログ管理ダイアログ ──
const CatalogDialog: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [catalog, setCatalog] = useState<CatalogApp[]>([]);
  const [form, setForm] = useState({ name: '', package_name: '', apk_url: '', note: '', version_code: '' });
  const [busy, setBusy] = useState(false);
  const load = () => listCatalog().then(setCatalog).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!form.name || !form.package_name || !form.apk_url) { alert('名称・パッケージ名・APK URL は必須です'); return; }
    setBusy(true);
    try {
      await addCatalogApp({ name: form.name, package_name: form.package_name, apk_url: form.apk_url, note: form.note, version_code: form.version_code ? Number(form.version_code) : 0 });
      setForm({ name: '', package_name: '', apk_url: '', note: '', version_code: '' }); load();
    }
    catch (e: any) { alert(e?.message ?? '追加に失敗しました'); }
    setBusy(false);
  };
  const del = async (id: string) => { if (!window.confirm('削除しますか？')) return; try { await deleteCatalogApp(id); load(); } catch { /* ignore */ } };
  const pushAll = async (a: CatalogApp) => {
    if (!window.confirm(`「${a.name}」(versionCode ${a.version_code}) を全台へ配信/更新しますか？\n各端末が現行より古い場合のみ自動でサイレント更新されます。`)) return;
    try { const r = await installToAll([a.id]); alert(`${r.queued}台へ配信キューに追加しました。オンライン端末から順次更新されます。`); }
    catch (e: any) { alert(e?.message ?? '配信に失敗しました'); }
  };
  const inp: React.CSSProperties = { width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' };
  return (
    <Overlay onClose={onClose} width={560}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>アプリカタログ</div>
        <button onClick={onClose} style={{ marginLeft: 'auto', border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={18} /></button>
      </div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 14 }}>端末へサイレント導入できるアプリを登録します。APKは <b>https:// の信頼できる配布元</b>（各アプリ公式・自社配布）のURLを指定してください。</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
        {catalog.map(a => (
          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{a.name} <span style={{ fontSize: 11, fontWeight: 500, color: '#7c3aed' }}>v{a.version_code || '—'}</span></div>
              <div style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.package_name} — {a.apk_url}</div>
            </div>
            <button onClick={() => pushAll(a)} style={{ border: 'none', background: '#7c3aed', color: '#fff', borderRadius: 7, padding: '6px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>全台配信</button>
            <button onClick={() => del(a.id)} style={{ border: '1px solid #e2e8f0', background: 'none', borderRadius: 7, padding: '5px 7px', cursor: 'pointer', color: '#94a3b8' }}><Trash2 size={13} /></button>
          </div>
        ))}
      </div>
      <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 7 }}>
        <input style={inp} placeholder="表示名（例: 自社点検アプリ）" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
        <input style={inp} placeholder="パッケージ名（例: jp.co.example.app）" value={form.package_name} onChange={e => setForm(f => ({ ...f, package_name: e.target.value }))} />
        <input style={inp} placeholder="APK URL（https://...）" value={form.apk_url} onChange={e => setForm(f => ({ ...f, apk_url: e.target.value }))} />
        <input style={inp} type="number" placeholder="versionCode（更新配信に必須・例: 12）" value={form.version_code} onChange={e => setForm(f => ({ ...f, version_code: e.target.value }))} />
        <button onClick={add} disabled={busy} style={{ padding: '10px', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>カタログに追加</button>
      </div>
    </Overlay>
  );
};

export default DevicesPage;
